import { useState } from 'react';
import { Check, ChevronRight, Scale } from 'lucide-react';
import { useTeamResolutions, type ResolutionRecord } from '../../hooks/useTeamResolutions';
import { recordTitle } from '../surfaces/ActionsSurface';
import { formatSourceDate } from '../../lib/formatting';
import { askNavTarget, askPrefillFor } from '../../lib/brief-card-exits';
import { RdCardDialog, type RdCardConfig } from './RdCardDialog';

/*
 * DecisionLedger — the mock's "Decision ledger / Settled direction." panel (brief §7), on the REAL
 * record rail (GET /api/surfaces/team/resolutions). Self-contained fetch, mirroring the modals'
 * pattern: the brief's route wrapper stays single-hook. Static section sizing (Teddy 2026-07-27):
 * the shell ALWAYS renders on the live brief — loading shows ghost rows, an error or an empty
 * record shows an honest line; the section never collapses out of the layout.
 *
 * Shows resolved + disputed settlements (a contested record row is still a record row — the mock's
 * "Re-resolve" case); dismissed ("not a conflict") entries stay off the brief and live on the
 * Actions record. Row click opens the record card — honest verbs only: open the two source
 * conversations, or the full record on Actions. Dates are ABSOLUTE (a settlement is a durable
 * fact, unlike the brief's live items — see brief-card-dates for the live-relative rule).
 */

const LEDGER_LIMIT = 3;

function recordCard(r: ResolutionRecord, onNav: (id: string) => void): RdCardConfig {
  const positions = [
    r.decisionAText
      ? {
          title: r.authorAName ? `${r.authorAName} · position` : 'Position A',
          note: r.decisionAText,
          tag: 'Position',
        }
      : null,
    r.decisionBText
      ? {
          title: r.authorBName ? `${r.authorBName} · position` : 'Position B',
          note: r.decisionBText,
          tag: 'Position',
        }
      : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);
  const sameConv = r.conversationA === r.conversationB;
  return {
    icon: <Scale size={19} />,
    eyebrow: r.status === 'disputed' ? 'Decision record · disputed' : 'Decision record',
    // Short subject, same rule as the Actions record card — the settled SENTENCE goes in the body
    // below rather than being the headline, so a record row is scannable at a glance.
    title: recordTitle(r),
    description:
      [
        r.settledText,
        r.rationale,
        r.resolvedByName ? `Settled by ${r.resolvedByName} · ${formatSourceDate(r.resolvedAt) ?? ''}` : formatSourceDate(r.resolvedAt),
        r.status === 'disputed' && r.disputeNote ? `Disputed: “${r.disputeNote}”` : null,
      ]
        .filter(Boolean)
        .join('-') || null,
    itemsLabel: positions.length > 0 ? 'The two positions' : undefined,
    items: positions,
    actionsLabel: 'The record',
    // C1: the contract's exits come from `primary` below, so `actions` now carries only what this
    // card has BEYOND the contract. The A-side note used to be an action tile of its own ("Open the
    // source" / "Open source A"); it IS the standard [Open the note] exit now, and keeping the tile
    // would render the same navigation twice. The B side has no standard slot — a settlement is the
    // one card with two notes — so it stays, and so does the full record.
    actions: [
      ...(sameConv
        ? []
        : [{ label: 'Open source B', icon: ChevronRight, onClick: () => onNav('conversations/' + r.conversationB) }]),
      { label: 'Full record on Actions', icon: Scale, onClick: () => onNav('actions') },
    ],
    primary: {
      sourceTargets: null,
      externalUrl: null,
      noteConversationId: r.conversationA,
      askPrefill: askPrefillFor(recordTitle(r)),
      onNavNote: (conversationId: string) => onNav('conversations/' + conversationId),
      onAsk: (prefill: string) => onNav(askNavTarget(prefill)),
    },
  };
}

export function DecisionLedger({ onNav }: { onNav: (id: string) => void }) {
  // isPending (v5: status === 'pending'), NOT isLoading (isPending && isFetching): a query that
  // mounts offline sits pending/PAUSED — isLoading is false there, and the empty branch would
  // claim "No settled decisions" about a record that was never read. Pending → ghost rows.
  const { data, isPending, error } = useTeamResolutions();
  const [openCard, setOpenCard] = useState<RdCardConfig | null>(null);

  // A row must be able to NAME what settled (Teddy 2026-07-28): a pointer-churned or
  // privacy-thinned record with neither a typed answer nor a topic would render as a blank
  // "Settled decision" placeholder — it stays off the brief and lives on the Actions full record.
  const rows = (data?.resolutions ?? [])
    .filter((r) => r.status !== 'dismissed' && Boolean(r.settledText || r.topic))
    .slice(0, LEDGER_LIMIT);

  return (
    <section className="h-full rounded-[24px] border border-[#2a2447] bg-[#322b4d] p-5 text-[#fbf5e9] shadow-[0_18px_38px_rgba(74,58,107,.16)] sm:p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-[#c7c2d4]">Decision ledger</p>
          <h2 className="mt-1 font-['Instrument_Serif'] text-[34px] leading-8">Settled direction.</h2>
        </div>
        <button
          onClick={() => onNav('actions')}
          className="rounded-full border border-white/15 bg-white/[.06] px-2.5 py-1 font-['DM_Mono'] text-[9px] text-[#c7c2d4] transition hover:bg-white/[.12] hover:text-white"
        >
          FULL RECORD ›
        </button>
      </div>
      <p className="mt-3 max-w-lg text-sm leading-5 text-[#c2bcd2]">
        Only the choices that change how work moves next.
      </p>
      {isPending && (
        <div className="mt-5 space-y-3 border-y border-white/10 py-4">
          {[0, 1, 2].map((i) => (
            <span key={i} className="block h-4 w-2/3 animate-pulse rounded bg-white/10" />
          ))}
        </div>
      )}
      {!isPending && rows.length === 0 && (
        <p className="mt-5 rounded-2xl border border-dashed border-white/20 p-4 text-sm text-[#c2bcd2]">
          {error ? 'The record is unavailable right now.' : 'No settled decisions on the record yet.'}
        </p>
      )}
      {rows.length > 0 && (
      <div className="mt-5 divide-y divide-white/10 border-y border-white/10">
        {/* bug-720: the row grid uses minmax(0,1fr), never bare 1fr — the truncate children
          * floored the track at their full one-line width, widening the ledger past the viewport. */}
        {rows.map((r) => (
          <button
            key={r.id}
            onClick={() => setOpenCard(recordCard(r, onNav))}
            className="group grid w-full grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-3 py-4 text-left transition hover:bg-white/[.045]"
          >
            <span className="grid size-7 place-items-center rounded-full border border-[#8c85a8] bg-[#3c3358] text-[#ded7ec]">
              {r.status === 'disputed' ? <Scale size={13} strokeWidth={2.4} /> : <Check size={13} strokeWidth={2.7} />}
            </span>
            <span className="min-w-0">
              {/* One-line rows (Teddy 2026-07-28): the row is a scannable label; the full settled
                * text + rationale live in the opened record card. */}
              <span className="block truncate text-sm font-semibold leading-5">
                {recordTitle(r)}
                {r.status === 'disputed' && <span className="ml-2 font-normal text-[#e0a3a3]">disputed</span>}
              </span>
              {(r.settledText || r.rationale) && (
                <span className="mt-1 block truncate text-xs text-[#c2bcd2]">{r.settledText ?? r.rationale}</span>
              )}
            </span>
            <span className="flex items-center gap-2">
              <span className="hidden whitespace-nowrap font-['DM_Mono'] text-[9px] text-[#c7c2d4] sm:inline">
                {formatSourceDate(r.resolvedAt)}
              </span>
              <span className="grid size-6 place-items-center rounded-full border border-white/10 bg-white/[.06] transition group-hover:bg-[#ded7ec] group-hover:text-[#322b4d]">
                <ChevronRight size={13} />
              </span>
            </span>
          </button>
        ))}
      </div>
      )}
      {openCard && <RdCardDialog config={openCard} onClose={() => setOpenCard(null)} />}
    </section>
  );
}
